import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  CheckCircle2,
  LockKeyhole,
  Megaphone,
  ShieldCheck,
  Smartphone,
  WalletCards,
} from "lucide-react";

const proofLogos = ["MoniePilot", "CrediPay", "TradeBase", "SwiftSub", "LagosGrid", "AjoPay", "MarketOS"];

const proofRail = [
  { label: "Escrow funded", value: "₦12.8m", detail: "budget secured" },
  { label: "BRM network live", value: "418", detail: "trusted promoters" },
  { label: "Users verified", value: "263k", detail: "proof-backed enrollments" },
  { label: "Webhook matched", value: "98.4%", detail: "conversion confidence" },
  { label: "Payouts ready", value: "₦4.2m", detail: "released after proof" },
  { label: "Campaign ROI", value: "3.7x", detail: "measured per channel" },
];

const heroStackCards = [
  {
    title: "Campaign validation",
    rows: ["Escrow funded", "Product rule set", "Webhook connected"],
    badge: "Ready",
  },
  {
    title: "Matching BRMs",
    rows: ["Fintech audience", "Lagos network", "Top BRMs selected"],
    badge: "418 matched",
  },
  {
    title: "Proof & conversion",
    rows: ["KYC complete", "Subscription active", "Duplicate check passed"],
    badge: "98.4%",
  },
  {
    title: "Payment scheduled",
    rows: ["BRM commission", "Client approval", "Escrow release"],
    badge: "₦4.2m",
  },
];

const heroAnnotations = [
  {
    audience: "For founders",
    text: "Don't buy clicks. Fund verified revenue actions.",
    left: "48%",
    bottom: "255px",
    delay: "0s",
  },
  {
    audience: "For growth teams",
    text: "You don't need to chase big influencers. Activate trusted BRMs already close to buyers.",
    left: "58%",
    bottom: "145px",
    delay: "5s",
  },
  {
    audience: "For BRMs",
    text: "Promote clear offers, track every referral, and earn only from proven outcomes.",
    left: "42%",
    bottom: "185px",
    delay: "10s",
  },
  {
    audience: "For finance",
    text: "Escrow keeps budget controlled while proof decides when payouts move.",
    left: "63%",
    bottom: "230px",
    delay: "15s",
  },
];

const workflowCards = [
  {
    icon: WalletCards,
    title: "Escrow first",
    copy: "Clients fund a conversion budget before BRMs start promoting. No unclear promises, no unpaid field work.",
  },
  {
    icon: Megaphone,
    title: "BRMs activate demand",
    copy: "Verified business relationship managers promote offers with referral links and clear payout rules.",
  },
  {
    icon: BadgeCheck,
    title: "Proof before payout",
    copy: "Proovam verifies subscription or enrollment events through API, webhook, and campaign checks.",
  },
  {
    icon: BarChart3,
    title: "Scale what works",
    copy: "Teams see BRM performance, campaign ROI, verified users, and payout readiness from one dashboard.",
  },
];

const userSections = [
  {
    eyebrow: "For founders",
    title: "Launch growth without hiring a full field team.",
    copy: "Proovam gives early and scaling teams a distribution network that is paid on results, not on retainers, impressions, or hope.",
    bullets: ["Fund one campaign", "Verify each user", "Pay only after proof"],
    image: "/landing/proovam-founders-brms.png",
  },
  {
    eyebrow: "For BRMs",
    title: "Turn trusted relationships into transparent income.",
    copy: "BRMs get clear offers, referral tracking, wallet visibility, and campaign guidance so every effort can be connected to a real payout.",
    bullets: ["Promote verified offers", "Track every referral", "Earn from completed goals"],
    image: "/landing/proovam-flow-infographic.png",
  },
];

const metrics = [
  { label: "Verified users", value: "263k", note: "ready for enrollment-style campaigns" },
  { label: "Campaign model", value: "RaaS", note: "results as a service, not ad spend" },
  { label: "Payout rule", value: "Proof", note: "conversion verified before release" },
];

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen overflow-x-hidden bg-white font-sans text-[#050505]">
      <section className="relative overflow-hidden rounded-b-[18px] bg-[#07111f] text-white shadow-[0_18px_70px_rgba(5,10,25,0.2)]">
        <picture>
          <source
            type="image/jpeg"
            srcSet="/landing/proovam-hero-city-768.jpg 768w, /landing/proovam-hero-city-1200.jpg 1200w, /landing/proovam-hero-city-1600.jpg 1600w"
            sizes="100vw"
          />
          <img
            src="/landing/proovam-hero-city-1200.jpg"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
        </picture>
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(9,24,31,0.9)_0%,rgba(9,24,31,0.7)_42%,rgba(9,24,31,0.36)_100%)]" />
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-white/80 via-white/35 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/80 to-transparent" />

        <header className="relative z-20 mx-auto flex w-[min(1200px,calc(100%-32px))] items-center justify-between px-1 py-7">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-black text-base font-black text-white shadow-sm">
              P
            </span>
            <span className="hidden text-lg font-semibold tracking-[0] text-black sm:inline">Proovam</span>
          </button>
          <nav className="hidden items-center gap-9 text-[15px] font-medium text-black lg:flex">
            <a href="#clients" className="hover:text-brand-500">Solutions</a>
            <a href="#brms" className="hover:text-brand-500">BRMs</a>
            <a href="#workflow" className="hover:text-brand-500">Platform</a>
            <a href="#problem" className="hover:text-brand-500">Problem</a>
            <a href="#founders" className="hover:text-brand-500">Founders</a>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => navigate("/client/login")}
              className="rounded-xl border border-black/10 bg-white/80 px-2.5 py-2 text-xs font-medium text-black shadow-theme-xs backdrop-blur transition hover:bg-white sm:px-4 sm:text-sm"
            >
              Founder sign in
            </button>
            <button
              onClick={() => navigate('/brm/login')}
              className="rounded-xl border border-white/30 bg-[#07111f]/80 px-2.5 py-2 text-xs font-medium text-white transition hover:bg-[#07111f] sm:px-4 sm:text-sm"
            >
              Agent sign in
            </button>
            <button
              onClick={() => navigate("/client/register")}
              className="hidden rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(70,95,255,0.28)] transition hover:bg-brand-600 sm:inline-flex sm:px-5"
            >
              Start campaign
            </button>
          </div>
        </header>

        <div className="relative z-10 mx-auto min-h-[720px] w-full max-w-[1200px] px-6 pb-6 pt-14 md:px-8 lg:min-h-[820px]">
          <div className="grid items-start gap-10 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="max-w-[560px] pt-3 md:pt-8">
              <h1 className="text-[38px] font-normal leading-[1.08] tracking-[0] text-white md:text-[54px] lg:text-[58px]">
                Find customers. Know what worked.
              </h1>
              <p className="mt-5 max-w-[540px] text-sm leading-6 text-white/68 md:text-base">
                Grow subscriptions as a founder, or earn by introducing customers as an Agent. Proovam checks the subscription before a reward is paid.
              </p>
              <div className="mt-7 grid gap-3 sm:grid-cols-2" aria-label="Choose your Proovam path">
                <div className="rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur-md">
                  <p className="text-sm font-semibold text-white">I am a founder</p>
                  <p className="mt-1 text-xs leading-5 text-white/70">I have a subscription product and want Agents to introduce customers.</p>
                  <button onClick={() => navigate('/client/register')} className="mt-4 rounded-lg bg-white px-4 py-2 text-xs font-semibold text-[#07111f]">Launch a campaign</button>
                </div>
                <div className="rounded-xl border border-brand-300/50 bg-brand-500/20 p-4 backdrop-blur-md">
                  <p className="text-sm font-semibold text-white">I am an Agent (BRM)</p>
                  <p className="mt-1 text-xs leading-5 text-white/70">I introduce customers, track confirmed subscriptions, and earn rewards.</p>
                  <button onClick={() => navigate('/brm/login')} className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-xs font-semibold text-white">Earn as an Agent</button>
                </div>
              </div>
              <div className="mt-7 flex max-w-[470px] gap-2 rounded-md border border-white/14 bg-white/8 p-1 backdrop-blur-md">
                <input
                  aria-label="Work email"
                  placeholder="Enter your work email"
                  className="h-10 min-w-0 flex-1 bg-transparent px-3 text-xs text-white outline-none placeholder:text-white/42"
                />
                <button
                  onClick={() => navigate("/client/register")}
                  className="h-10 rounded bg-white px-5 text-xs font-semibold text-black transition hover:bg-white/90"
                >
                  See a live demo
                </button>
              </div>
            </div>

            <div className="relative hidden min-h-[360px] lg:block">
              <div className="absolute right-0 top-5 h-[270px] w-[470px]">
                {heroStackCards.map((card, index) => (
                  <div
                    key={card.title}
                    className="proovam-hero-workflow-card absolute right-0 top-0 w-[360px] rounded-[14px] border border-white/10 bg-[#2c3940]/72 p-4 text-white shadow-[0_20px_70px_rgba(0,0,0,0.3)] backdrop-blur-xl"
                    style={{ animationDelay: `${index * 3}s` }}
                  >
                    <div className="mb-3 flex items-center gap-3">
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white/10">
                        <BadgeCheck className="h-4 w-4 text-emerald-300" />
                      </span>
                      <p className="text-sm font-semibold text-white/84">{card.title}</p>
                    </div>
                    <div className="space-y-2">
                      {card.rows.map((row) => (
                        <div key={row} className="flex items-center gap-2 rounded-md bg-black/18 px-3 py-2">
                          <span className="h-2 w-2 rounded-full bg-lime-300" />
                          <span className="text-xs text-white/72">{row}</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 inline-flex rounded-full bg-white/12 px-3 py-1 text-[11px] font-semibold text-white/72">
                      {card.badge}
                    </p>
                  </div>
                ))}
              </div>

              <div className="proovam-hero-alert absolute right-7 top-[285px] flex w-[330px] items-center gap-3 rounded-[12px] border border-white/10 bg-[#2d3941]/72 p-3 text-white shadow-[0_18px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10">
                  <ShieldCheck className="h-5 w-5 text-brand-100" />
                </span>
                <div>
                  <p className="text-xs font-semibold text-white/86">Payout scheduled</p>
                  <p className="text-[11px] text-white/50">BRM commission approved after proof</p>
                </div>
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute inset-0 z-10">
            {heroAnnotations.map((annotation) => (
              <div
                key={annotation.text}
                className="proovam-hero-annotation absolute rounded-[14px] border border-white/10 bg-[#2c3940]/72 p-4 text-white shadow-[0_18px_58px_rgba(0,0,0,0.28)] backdrop-blur-xl"
                style={{
                  "--annotation-left": annotation.left,
                  "--annotation-bottom": annotation.bottom,
                  animationDelay: annotation.delay,
                }}
              >
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-100/80">{annotation.audience}</p>
                <p className="text-sm font-medium leading-5 text-white/82">{annotation.text}</p>
              </div>
            ))}
          </div>

          <div className="absolute bottom-8 left-6 right-6 md:left-8 md:right-8">
            <div className="proovam-mask-x overflow-hidden">
              <div className="proovam-marquee flex w-max items-center gap-10 pr-10 text-lg font-semibold text-white/76">
                {[...proofLogos, ...proofLogos].map((logo, index) => (
                  <div key={`${logo}-${index}`} className="flex min-w-max items-center gap-3 opacity-80">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/18 bg-white/10 text-[10px] font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] backdrop-blur">
                      {logo.slice(0, 2)}
                    </span>
                    <span>{logo}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="clients" className="px-6 py-24 md:py-28">
        <div className="mx-auto max-w-5xl text-center">
          <p className="mb-7 text-sm text-gray-400">End-to-end verified growth for ambitious teams</p>
          <h2 className="text-[42px] font-normal leading-[1.08] tracking-[0] text-black md:text-[54px]">
            Every referral, every subscription, handled by Proovam proof workflows
          </h2>
        </div>
        <div className="mx-auto mt-10 max-w-[760px]">
          <div className="mx-auto flex h-9 max-w-[560px] items-center rounded-full bg-gray-100 p-1">
            <div className="h-7 w-1/3 rounded-full bg-gray-400" />
            <div className="flex flex-1 justify-end pr-3">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-gray-600 shadow-sm">
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </div>
        </div>
        <div className="mx-auto mt-14 max-w-7xl">
          <div className="proovam-mask-x overflow-hidden">
            <div className="proovam-marquee-slow flex w-max gap-4 pr-4">
              {[...proofRail, ...proofRail].map((item, index) => (
                <div
                  key={`${item.label}-${index}`}
                  className="grid min-h-[154px] w-[280px] content-between rounded-[22px] border border-gray-200 bg-white p-5 text-left shadow-[0_18px_50px_rgba(16,24,40,0.08)] md:w-[340px]"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-600">
                      Live proof
                    </span>
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-[0_0_0_6px_rgba(16,185,129,0.12)]" />
                  </div>
                  <div>
                    <p className="text-[34px] font-semibold leading-none tracking-[0] text-gray-950">{item.value}</p>
                    <p className="mt-3 text-base font-semibold text-gray-800">{item.label}</p>
                    <p className="mt-1 text-sm text-gray-500">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto mt-16 max-w-6xl overflow-hidden rounded-[26px] border border-gray-200 bg-white shadow-[0_22px_70px_rgba(16,24,40,0.12)]">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr]">
            <div className="p-8 md:p-12">
              <p className="mb-5 inline-flex rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-600">Proovam dashboard</p>
              <h3 className="text-3xl font-semibold text-gray-950 md:text-4xl">One operating system for funded offers, BRMs, and verified conversions.</h3>
              <p className="mt-5 text-lg leading-8 text-gray-500">
                Clients can create Prove-Requests, fund escrow, inspect enrollments, manage BRMs, and connect verification APIs without stitching spreadsheets together.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {["Campaign ROI", "Escrow health", "Verified users", "BRM performance"].map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm font-medium text-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-brand-500" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative min-h-[420px] bg-gray-950 p-4">
              <video
                src="/landing/proovam-dashboard-loop.mp4"
                poster="/landing/proovam-dashboard-mockup.png"
                className="h-full min-h-[420px] w-full rounded-[18px] object-cover"
                autoPlay
                muted
                loop
                playsInline
                aria-label="Animated Proovam dashboard preview"
              />
              <div className="absolute right-8 top-8 rounded-2xl border border-white/18 bg-white/12 p-4 text-white backdrop-blur-xl">
                <p className="text-sm text-white/60">Live verification</p>
                <p className="mt-1 text-2xl font-semibold">98.4%</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="workflow" className="bg-[#f8f9fb] px-6 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="mb-4 text-sm text-gray-400">How Proovam works</p>
              <h2 className="max-w-3xl text-4xl font-normal leading-tight md:text-6xl">A clean loop for performance-led growth</h2>
            </div>
            <button onClick={() => navigate("/client/register")} className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/20">
              Build your first campaign <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {workflowCards.map((card) => (
              <article key={card.title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-[0_10px_30px_rgba(16,24,40,0.06)]">
                <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-500">
                  <card.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-950">{card.title}</h3>
                <p className="mt-3 leading-7 text-gray-500">{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="problem" className="px-6 py-24">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div>
            <p className="mb-4 text-sm text-gray-400">The problem</p>
            <h2 className="text-4xl font-normal leading-tight md:text-6xl">Marketing spend should become measurable revenue, not a guessing game.</h2>
          </div>
          <div className="grid gap-4">
            {[
              "Clicks don't prove that someone subscribed.",
              "Big influencers are expensive and often disconnected from local trust.",
              "Field marketers need transparent payout rules before they promote.",
            ].map((item) => (
              <div key={item} className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-lg leading-8 text-gray-600">
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="founders" className="bg-gray-950 px-6 py-24 text-white">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm text-white/40">Who Proovam helps</p>
            <h2 className="text-4xl font-normal leading-tight md:text-6xl">Built for the teams and operators who turn trust into growth.</h2>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            {userSections.map((section) => (
              <article key={section.eyebrow} className="overflow-hidden rounded-[26px] border border-white/10 bg-white/8">
                <img src={section.image} alt="" className="h-72 w-full object-cover" />
                <div className="p-8">
                  <p className="text-sm font-medium text-brand-100">{section.eyebrow}</p>
                  <h3 className="mt-3 text-3xl font-semibold">{section.title}</h3>
                  <p className="mt-4 leading-7 text-white/58">{section.copy}</p>
                  <div className="mt-6 grid gap-3">
                    {section.bullets.map((bullet) => (
                      <div key={bullet} className="flex items-center gap-3 text-sm text-white/72">
                        <CheckCircle2 className="h-5 w-5 text-brand-100" />
                        {bullet}
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="brms" className="px-6 py-24">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <p className="mb-4 text-sm text-gray-400">Mobile-first for BRMs</p>
            <h2 className="text-4xl font-normal leading-tight">Campaign work that feels clear on the move.</h2>
          </div>
          <div className="grid gap-5 lg:col-span-2 sm:grid-cols-3">
            {[
              { icon: Smartphone, title: "Referral links", copy: "Share campaign URLs with each relationship." },
              { icon: LockKeyhole, title: "Wallet clarity", copy: "See pending and released commissions." },
              { icon: ShieldCheck, title: "Proof trail", copy: "Know exactly why a conversion paid out." },
            ].map((item) => (
              <article key={item.title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-[0_10px_30px_rgba(16,24,40,0.06)]">
                <item.icon className="h-7 w-7 text-brand-500" />
                <h3 className="mt-8 text-xl font-semibold">{item.title}</h3>
                <p className="mt-3 leading-7 text-gray-500">{item.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto overflow-hidden rounded-[28px] bg-brand-500 px-8 py-16 text-white md:px-14">
          <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <h2 className="max-w-3xl text-4xl font-normal leading-tight md:text-6xl">Ready to fund growth that proves itself?</h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-white/72">
                Create your first Prove-Request, invite BRMs, and connect verification without rebuilding your growth stack.
              </p>
            </div>
            <button onClick={() => navigate("/client/register")} className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-white px-6 text-sm font-semibold text-brand-600 shadow-lg">
              Start campaign <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-100 px-6 py-10">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500 text-lg font-black text-white">P</span>
            <span className="text-xl font-semibold text-gray-950">Proovam</span>
          </div>
          <p className="text-sm text-gray-400">Built for verified distribution. © 2026 Proovam.</p>
          <div className="flex gap-5 text-sm text-gray-500">
            <Link to="/client/login" className="hover:text-brand-500">Client login</Link>
            <Link to="/brm/login" className="hover:text-brand-500">BRM login</Link>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default LandingPage;
